import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { useAnimation } from '../../../hooks/useAnimation';

export default function ComplexLayout() {
  const [isZoomed, setIsZoomed] = useState(false);

  const highlights = [
    {
      icon: 'ri-building-2-line',
      title: '2개 동',
      desc: '101동 · 102동',
    },
    {
      icon: 'ri-home-4-line',
      title: '총 세대수',
      desc: '프리미엄 주거 단지',
    },
    {
      icon: 'ri-compass-3-line',
      title: '전세대 남향',
      desc: '최적의 채광 설계',
    },
    {
      icon: 'ri-tree-line',
      title: '중앙 조경',
      desc: '풍부한 녹지 공간',
    },
    {
      icon: 'ri-parking-box-line',
      title: '지하 주차장',
      desc: '세대당 충분한 주차',
    },
    {
      icon: 'ri-shield-check-line',
      title: '보안 시스템',
      desc: '24시간 무인 경비',
    },
  ];

  const facilities = [
    { label: '헬기장', icon: 'ri-flight-takeoff-line', color: 'bg-[#1a2b5c]' },
    { label: '중앙 광장', icon: 'ri-landscape-line', color: 'bg-emerald-600' },
    { label: '조경 공원', icon: 'ri-tree-line', color: 'bg-green-500' },
    { label: '지하 주차장', icon: 'ri-parking-box-line', color: 'bg-gray-600' },
    { label: '커뮤니티 시설', icon: 'ri-community-line', color: 'bg-amber-600' },
    { label: '보행자 통로', icon: 'ri-walk-line', color: 'bg-sky-600' },
  ];

  const introRef = useAnimation('fade-in-up');
  const highlightsRef = useAnimation('stagger-children');
  const mapRef = useAnimation('fade-in');
  const facilitiesRef = useAnimation('stagger-children');
  const buildingsRef = useAnimation('stagger-children');
  const ctaRef = useAnimation('fade-in-up');

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Page Header */}
      <section className="relative h-[180px] md:h-[400px] flex items-center justify-center overflow-hidden mt-16 md:mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/97c9749d0442cd8df4f07780a017d274.jpg"
            alt="단지배치도"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2b5c]/80 via-[#1a2b5c]/65 to-[#1a2b5c]/80"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full">
          <h1 className="text-[20px] md:text-[48px] font-bold text-white mb-2 md:mb-4" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            단지배치도
          </h1>
          <p className="text-[11px] md:text-[18px] text-white/90">
            자연과 함께하는 프리미엄 단지 설계
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-2 text-[11px] md:text-[14px]">
            <Link to="/" className="text-gray-500 hover:text-[#1a2b5c] whitespace-nowrap">홈</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-500 whitespace-nowrap">단지안내</span>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-[#1a2b5c] font-medium whitespace-nowrap">단지배치도</span>
          </div>
        </div>
      </div>

      {/* Intro Title */}
      <section className="py-8 md:py-16 bg-white" ref={introRef}>
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center">
          <span className="inline-block px-3 md:px-6 py-1.5 md:py-2 bg-[#1a2b5c]/10 text-[#1a2b5c] rounded-full text-[10px] md:text-[14px] font-semibold mb-3 md:mb-5">
            단지배치도
          </span>
          <h2 className="text-[16px] sm:text-[24px] md:text-[40px] font-bold text-[#1a2b5c] mb-3 md:mb-5 leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            쾌적하고 안전한<br />
            <span className="text-[#2d4a8f]">프리미엄 단지 설계</span>
          </h2>
          <p className="text-[11px] md:text-[17px] text-gray-600 leading-relaxed max-w-[700px] mx-auto">
            전세대 남향 배치와 풍부한 중앙 조경으로 완성된<br />
            가경 센트럴 더 플래티넘의 단지 배치를 확인하세요.
          </p>
        </div>
      </section>

      {/* Highlight Stats */}
      <section className="pb-6 md:pb-12 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-4" ref={highlightsRef}>
            {highlights.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-100 rounded-xl md:rounded-2xl p-2 md:p-5 shadow-md hover:shadow-xl transition-all text-center group cursor-default animate-item"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className="w-8 h-8 md:w-12 md:h-12 bg-[#1a2b5c]/10 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-1.5 md:mb-3 group-hover:bg-[#1a2b5c] transition-colors">
                  <i className={`${item.icon} text-[#1a2b5c] text-base md:text-2xl group-hover:text-white transition-colors`}></i>
                </div>
                <p className="text-[10px] md:text-[15px] font-bold text-[#1a2b5c] mb-0.5 md:mb-1">{item.title}</p>
                <p className="text-[9px] md:text-[12px] text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Map Image */}
      <section className="py-6 md:py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-3 md:mb-6">
            <h3 className="text-[14px] md:text-[22px] font-bold text-[#1a2b5c]" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              단지 배치도
            </h3>
            <button
              onClick={() => setIsZoomed(!isZoomed)}
              className="flex items-center gap-1 md:gap-2 px-3 md:px-5 py-1.5 md:py-2.5 bg-[#1a2b5c] text-white rounded-full text-[10px] md:text-[14px] font-medium hover:bg-[#2d4a8f] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className={`${isZoomed ? 'ri-zoom-out-line' : 'ri-zoom-in-line'}`}></i>
              {isZoomed ? '축소하기' : '크게 보기'}
            </button>
          </div>

          {/* Map Container */}
          <div
            ref={mapRef}
            className={`relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-gray-200 bg-white transition-all duration-500 cursor-pointer ${isZoomed ? 'fixed inset-4 z-50 rounded-2xl' : ''}`}
            onClick={() => isZoomed && setIsZoomed(false)}
          >
            {isZoomed && (
              <div className="absolute inset-0 bg-black/60 z-10 flex items-center justify-center" onClick={() => setIsZoomed(false)}>
                <div
                  className="relative bg-white rounded-2xl overflow-hidden shadow-2xl max-w-[90vw] max-h-[90vh] w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setIsZoomed(false)}
                    className="absolute top-4 right-4 z-20 w-10 h-10 bg-[#1a2b5c] text-white rounded-full flex items-center justify-center hover:bg-[#2d4a8f] transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                  <img
                    src="https://static.readdy.ai/image/3b7ebc9e513f68fc027b047fbdd0fbe7/80ef3d08f22661d0a7b850e7c55f99f4.png"
                    alt="단지배치도 확대"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
            )}

            {!isZoomed && (
              <img
                src="https://static.readdy.ai/image/3b7ebc9e513f68fc027b047fbdd0fbe7/80ef3d08f22661d0a7b850e7c55f99f4.png"
                alt="단지배치도"
                className="w-full h-auto object-contain"
              />
            )}
          </div>

          <p className="text-[10px] md:text-[13px] text-gray-400 mt-2 md:mt-4 text-center">
            ※ 상기 배치도는 설계 변경 등에 따라 변경될 수 있으며, 실제와 다를 수 있습니다.
          </p>
        </div>
      </section>

      {/* Facility Legend */}
      <section className="py-8 md:py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <h3 className="text-[16px] md:text-[28px] font-bold text-[#1a2b5c] mb-5 md:mb-10 text-center" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            주요 시설 안내
          </h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-5" ref={facilitiesRef}>
            {facilities.map((f, idx) => (
              <div 
                key={idx} 
                className="flex flex-col items-center gap-1.5 md:gap-3 bg-gray-50 rounded-xl md:rounded-2xl p-2 md:p-5 border border-gray-100 hover:shadow-md transition-all cursor-default animate-item"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className={`w-8 h-8 md:w-12 md:h-12 ${f.color} rounded-lg md:rounded-xl flex items-center justify-center`}>
                  <i className={`${f.icon} text-white text-base md:text-2xl`}></i>
                </div>
                <span className="text-[9px] md:text-[14px] font-semibold text-gray-700 text-center">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Building Info Cards */}
      <section className="py-8 md:py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <h3 className="text-[16px] md:text-[28px] font-bold text-[#1a2b5c] mb-5 md:mb-10 text-center" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            동별 안내
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8" ref={buildingsRef}>
            {[
              { dong: '101동', desc: '단지 서측에 위치하며 중앙 조경과 인접한 최적의 동선을 자랑합니다.', image: 'https://readdy.ai/api/search-image?query=Modern%20luxury%20high-rise%20apartment%20tower%20exterior%2C%20sleek%20glass%20facade%20residential%20building%20with%20elegant%20architecture%2C%20premium%20condominium%20tower%20with%20balconies%20and%20green%20surroundings%2C%20sophisticated%20navy%20blue%20sky%20background%2C%20professional%20real%20estate%20photography&width=800&height=600&seq=complex-dong-101&orientation=landscape', features: ['전세대 남향 배치', '중앙 조경 조망', '지하주차장 직결'] },
              { dong: '102동', desc: '단지 동측에 위치하며 탁 트인 조망과 풍부한 채광을 제공합니다.', image: 'https://readdy.ai/api/search-image?query=Luxury%20modern%20apartment%20building%20with%20glass%20curtain%20wall%2C%20premium%20residential%20tower%20with%20open%20sky%20view%2C%20elegant%20high-rise%20condominium%20with%20balconies%20and%20landscaping%2C%20clean%20minimalist%20architecture%20with%20warm%20lighting%2C%20professional%20architectural%20photography&width=800&height=600&seq=complex-dong-102&orientation=landscape', features: ['전세대 남향 배치', '개방감 있는 조망', '커뮤니티 시설 인접'] },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-xl border border-gray-100 group cursor-default animate-item"
                style={{ animationDelay: `${idx * 200}ms` }}
              >
                <div className="relative h-[140px] md:h-[260px] overflow-hidden">
                  <img src={item.image} alt={item.dong} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a2b5c]/80 via-[#1a2b5c]/30 to-transparent"></div>
                  <div className="absolute bottom-3 md:bottom-5 left-3 md:left-6">
                    <span className="text-[18px] md:text-[28px] font-bold text-white" style={{ fontFamily: "'Noto Serif KR', serif" }}>{item.dong}</span>
                  </div>
                </div>
                <div className="p-4 md:p-7">
                  <p className="text-[10px] md:text-[15px] text-gray-600 leading-relaxed mb-3 md:mb-5">{item.desc}</p>
                  <ul className="space-y-1.5 md:space-y-2">
                    {item.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 md:gap-3">
                        <i className="ri-checkbox-circle-fill text-[#1a2b5c] text-sm md:text-lg"></i>
                        <span className="text-[10px] md:text-[14px] text-gray-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-20 bg-gradient-to-br from-[#1a2b5c] to-[#0f1a3d] relative overflow-hidden" ref={ctaRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center relative z-10">
          <h2 className="text-[16px] md:text-[38px] font-bold text-white mb-3 md:mb-5" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            단지를 직접 확인해보세요
          </h2>
          <p className="text-[11px] md:text-[17px] text-white/85 mb-6 md:mb-10 leading-relaxed">
            모델하우스 방문을 통해 단지 배치와 세대 내부를<br />직접 경험하실 수 있습니다.
          </p>
          <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
            <Link
              to="/reservation"
              onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
              className="inline-flex items-center gap-1.5 md:gap-2 px-5 md:px-10 py-2.5 md:py-4 bg-white text-[#1a2b5c] rounded-full text-[11px] md:text-[16px] font-semibold hover:bg-gray-100 transition-all shadow-xl whitespace-nowrap"
            >
              방문예약 하기
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="tel:1555-5475"
              onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
              className="inline-flex items-center gap-1.5 md:gap-2 px-5 md:px-10 py-2.5 md:py-4 bg-transparent text-white rounded-full text-[11px] md:text-[16px] font-semibold hover:bg-white/10 transition-all border-2 border-white whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              전화 상담하기
            </a>
          </div>
        </div>
      </section>

      {/* 모바일 고정 플로팅 CTA 바 */}
      <div className="fixed bottom-0 left-0 right-0 z-[90] md:hidden">
        <div className="h-[2px] bg-gradient-to-r from-[#b8972a] via-[#f0d060] to-[#b8972a]"></div>
        <div className="bg-gradient-to-r from-[#0a1229] via-[#1a2b5c] to-[#0a1229] px-3 py-3 flex items-center gap-2.5 shadow-2xl">
          <Link
            to="/reservation"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group"
            style={{ background: 'linear-gradient(135deg, #b8972a 0%, #f0d060 40%, #c9a84c 70%, #b8972a 100%)' }}
          >
            <div className="absolute inset-0 bg-white/0 group-active:bg-white/10 transition-all"></div>
            <i className="ri-calendar-check-line text-[#0a1229] text-[18px]"></i>
            <span className="text-[14px] font-bold text-[#0a1229] relative z-10">방문예약</span>
          </Link>
          <a
            href="tel:1555-5475"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-white rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group border-2 border-white/30"
          >
            <div className="absolute inset-0 bg-[#1a2b5c]/0 group-active:bg-[#1a2b5c]/10 transition-all"></div>
            <i className="ri-phone-fill text-[#1a2b5c] text-[18px]"></i>
            <span className="text-[14px] font-bold text-[#1a2b5c] relative z-10">전화상담</span>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
